import { Loading } from '@/core/ui/components/Loading';

import { getErrorMessage, useSection, useServices, useToast } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import { useFileUpload } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { SystemSettings, TenantBillingInvoice, TenantFeatures } from '@/shared/types';
import { AlertTriangle, BarChart3, Bell, Book, Building, Calendar, Car, Clock, ClipboardList, CreditCard, Dog, DollarSign, FileText, Globe, Info, Key, Landmark, Lock, Mail, MapPin, MessageSquare, Package, Phone, Receipt, Save, Settings, Shield, ToggleLeft, Truck, Users, Vote, Wallet } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { IntegrationsTab } from '@/modules/settings/ui/components/IntegrationsTab';
import { SectionSelector } from '@/modules/sections/ui/components/SectionSelector';
import { SectionBankAccountCard } from '@/modules/sections/ui/components/SectionBankAccountCard';
import { BillingTab } from './BillingTab';
import { AddPaymentMethodModal, ChangePlanModal, InvoiceDetailModal, PayInvoiceModal } from './billing';

const SAT_REGIMES = [
  { code: '601', description: 'General de Ley Personas Morales' },
  { code: '603', description: 'Personas Morales con Fines no Lucrativos' },
  { code: '605', description: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { code: '606', description: 'Arrendamiento' },
  { code: '607', description: 'Régimen de Enajenación o Adquisición de Bienes' },
  { code: '608', description: 'Demás ingresos' },
  { code: '610', description: 'Residentes en el Extranjero sin Establecimiento Permanente en México' },
  { code: '611', description: 'Ingresos por Dividendos (socios y accionistas)' },
  { code: '612', description: 'Personas Físicas con Actividades Empresariales y Profesionales' },
  { code: '614', description: 'Ingresos por intereses' },
  { code: '615', description: 'Régimen de los ingresos por obtención de premios' },
  { code: '616', description: 'Sin obligaciones fiscales' },
  { code: '620', description: 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos' },
  { code: '621', description: 'Incorporación Fiscal' },
  { code: '622', description: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { code: '623', description: 'Opcional para Grupos de Sociedades' },
  { code: '624', description: 'Coordinados' },
  { code: '625', description: 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
  { code: '626', description: 'Régimen Simplificado de Confianza' }
];

export const AdminSettings = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { updateSystemSettings, systemSettings, settingsRepo } = useServices();
  const ability = useAbility();
  const canUpdateSettings = ability.can('manage', 'all')
    || ability.can('manage', 'Settings')
    || ability.can('update', 'Settings');
  // La cuenta de una sección la gestiona el admin global o el admin de esa sección
  // (el backend rechaza cualquier otra); el admin de sección no ve la del residencial.
  const { sections, isSectionAdmin: scopedToSection, selectedSectionId } = useSection();
  const canEditSectionBank = scopedToSection || ability.can('manage', 'all');
  const selectedSectionName = sections.find((section) => section.id === selectedSectionId)?.name ?? 'la sección';

  // Use local state for form editing, initialized from global context
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'GENERAL' | 'FINANCIAL' | 'INTEGRATIONS' | 'BILLING'>('GENERAL');

  // Billing modals state
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
  const [showPayInvoiceModal, setShowPayInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<TenantBillingInvoice | null>(null);
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);

  // File upload hook
  const { upload: uploadLogo, isUploading: isUploadingLogo } = useFileUpload('general', {
    allowedTypes: ['image/*'],
  });
  

  useEffect(() => {
    if (systemSettings) {
      setSettings(systemSettings);
      setLoading(false);
    }
  }, [systemSettings]);

  const handleChange = (field: keyof SystemSettings, value: any) => {
    if (!canUpdateSettings) return;
    if (settings) {
      setSettings({ ...settings, [field]: value });
    }
  };

  const handleBrandingChange = (key: string, value: string) => {
    if (!canUpdateSettings) return;
    if (settings) {
      setSettings({
        ...settings,
        branding: {
          ...settings.branding,
          [key]: value
        }
      });
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canUpdateSettings) return;
    const file = e.target.files?.[0];
    if (file && settings) {
      const result = await uploadLogo(file);
      if (result) {
        setSettings({
          ...settings,
          branding: {
            ...settings.branding,
            logoUrl: result.url,
            logoFileId: result.id,
          }
        });
      }
    }
  };

  const handleNestedChange = (section: 'bankInfo' | 'fiscalData', key: string, value: any) => {
     if (!canUpdateSettings) return;
     if (!settings) return;
     setSettings({
       ...settings,
       [section]: {
         ...(settings[section] as any || {}),
         [key]: value
       }
     });
  };

  const handleFeatureChange = (key: keyof TenantFeatures, value: boolean) => {
    if (!canUpdateSettings) return;
    if (!settings) return;
    setSettings({
      ...settings,
      features: {
        ...settings.features,
        [key]: value
      }
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canUpdateSettings) return;
    if (!settings) return;
    setSaving(true);
    try {
      await updateSystemSettings(settings);
      toast.success(t('admin.settings.success'));
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSection = async (section: string) => {
    if (!canUpdateSettings) return;
    if (!settings) return;
    setSavingSection(section);
    try {
      switch (section) {
        case 'branding':
          await settingsRepo.updateBrandingConfig({
            companyName: settings.branding?.appName || settings.condoName,
            logoUrl: settings.branding?.logoUrl,
            primaryColor: settings.branding?.primaryColor,
          });
          break;
        case 'identity':
          await settingsRepo.updateCompanyInfo({
            name: settings.condoName,
            legalName: settings.legalName || undefined,
            website: settings.website || undefined,
            address: settings.address,
            city: settings.city || undefined,
            state: settings.state || undefined,
            postalCode: settings.postalCode || undefined,
            country: settings.country || undefined,
            contactEmail: settings.contactEmail,
            contactPhone: settings.contactPhone,
            supportEmail: settings.supportEmail || undefined,
          });
          break;
        case 'rules':
          await settingsRepo.updateRules({
            // Financieros
            maintenanceFee: settings.defaultMaintenanceFee,
            lateFeePercentage: settings.lateFeePercentage,
            lateFeeGraceDays: settings.lateFeeGraceDays,
            paymentDueDay: settings.paymentDueDay,
            // Límites
            maxVehiclesPerUnit: settings.maxVehiclesPerUnit,
            maxPetsPerUnit: settings.maxPetsPerUnit,
            maxGuestsPerVisit: settings.visitorLimitPerUnit,
            maxActiveGuests: settings.maxActiveGuests,
            maxReservationsPerDay: settings.maxReservationsPerDay,
            maxAdvanceReservationDays: settings.maxAdvanceReservationDays,
            minCancellationHours: settings.minCancellationHours,
            // Contactos
            emergencyPhone: settings.emergencyPhone,
            securityPhone: settings.securityPhone || undefined,
            adminPhone: settings.adminPhone || undefined,
            // Horarios
            quietHoursStart: settings.quietHoursStart || undefined,
            quietHoursEnd: settings.quietHoursEnd || undefined,
            guestEntryStart: settings.guestEntryStart || undefined,
            guestEntryEnd: settings.guestEntryEnd || undefined,
          });
          break;
        case 'regional':
          await settingsRepo.updateLocalization({
            timezone: settings.timezone || 'America/Mexico_City',
            language: settings.language || 'es',
            dateFormat: settings.dateFormat || 'DD/MM/YYYY',
            timeFormat: settings.timeFormat || '24h',
            currency: settings.currency,
            currencySymbol: settings.currencySymbol || '$',
            firstDayOfWeek: settings.firstDayOfWeek ?? 1,
          });
          break;
        case 'features':
          await settingsRepo.updateModules({
            amenities: settings.features?.enableAmenities ?? true,
            guests: settings.features?.enableGuests ?? true,
            vehicles: settings.features?.enableVehicles ?? true,
            pets: settings.features?.enablePets ?? true,
            tickets: settings.features?.enableTickets ?? true,
            documents: settings.features?.enableDocuments ?? true,
            payments: settings.features?.enablePayments ?? true,
            announcements: settings.features?.enableCommunication ?? true,
            polls: settings.features?.enablePolls ?? false,
            accessControl: settings.features?.enableAccessControl ?? false,
            restrictions: settings.features?.enableRestrictions ?? false,
            emergency: settings.features?.enableEmergency ?? true,
            logbook: settings.features?.enableLogbook ?? true,
            patrol: settings.features?.enablePatrol ?? true,
            directory: settings.features?.enableDirectory ?? true,
            suppliers: settings.features?.enableSuppliers ?? true,
            inventory: settings.features?.enableInventory ?? true,
            reports: settings.features?.enableReports ?? true,
          });
          break;
        case 'bankInfo':
          await settingsRepo.updateBankInfo(settings.bankInfo || {});
          break;
        case 'fiscalData':
          await settingsRepo.updateFiscalData(settings.fiscalData || {});
          break;
      }
      toast.success(`${section} guardado correctamente`);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSavingSection(null);
    }
  };

  if (loading || !settings) return <Loading />;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-slate-800 text-2xl">{t('admin.settings.title')}</h3>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTab('GENERAL')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'GENERAL' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('admin.settings.tab.general')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('FINANCIAL')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'FINANCIAL' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('admin.settings.tab.financial')}
          </button>
          {/* Integraciones y facturación son del ADMIN: en solo lectura no se muestran. */}
          {canUpdateSettings && (<>
          <button
            type="button"
            onClick={() => setActiveTab('INTEGRATIONS')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'INTEGRATIONS' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('admin.settings.tab.integrations')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('BILLING')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'BILLING' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Wallet size={16} />
            Facturacion
          </button>
          </>)}
        </div>

        {/* GENERAL TAB */}
        {activeTab === 'GENERAL' && (
          <div className="space-y-6 animate-in fade-in">
            
            {/* Branding Section */}
            {/* <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-slate-800 flex items-center gap-2">
                  <div className="p-1.5 bg-pink-50 rounded text-pink-600"><Palette size={18} /></div>
                  Identidad Visual (Branding)
                </h4>
                <Button
                  size="sm"
                  variant="primary"
                  icon={<Save size={16} />}
                  isLoading={savingSection === 'branding'}
                  onClick={() => handleSaveSection('branding')}
                >
                  Guardar
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Logotipo de la App</label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                      {settings.branding?.logoUrl ? (
                        <img src={settings.branding.logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                      ) : (
                        <Image className="text-slate-300" />
                      )}
                    </div>
                    <label className="cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-slate-300 shadow-sm text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors">
                      <Upload size={16} className="mr-2 text-slate-500" />
                      Subir Logo
                      <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Color Primario</label>
                  <div className="flex gap-3">
                    <input 
                      type="color" 
                      value={settings.branding?.primaryColor || '#8B5CF6'}
                      onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
                      className="h-10 w-16 rounded cursor-pointer border border-slate-300"
                    />
                    <Input 
                      value={settings.branding?.primaryColor || '#8B5CF6'}
                      onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <Input 
                  label="Nombre de la Aplicación" 
                  value={settings.branding?.appName || 'Hommi'} 
                  onChange={e => handleBrandingChange('appName', e.target.value)} 
                />
              </div>
            </div> */}

            {/* Identity Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-slate-800 flex items-center gap-2">
                  <div className="p-1.5 bg-blue-50 rounded text-blue-600"><Building size={18} /></div>
                  {t('admin.settings.section.identity')}
                </h4>
                {canUpdateSettings && (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Save size={16} />}
                    isLoading={savingSection === 'identity'}
                    onClick={() => handleSaveSection('identity')}
                  >
                    Guardar
                  </Button>
                )}
              </div>
              <fieldset disabled={!canUpdateSettings} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label={t('admin.settings.condo_name')}
                  value={settings.condoName}
                  onChange={e => handleChange('condoName', e.target.value)}
                />
                <Input
                  label="Razón Social"
                  value={settings.legalName || ''}
                  onChange={e => handleChange('legalName', e.target.value)}
                />
                <Input
                  label={t('admin.settings.website')}
                  icon={<Globe size={16}/>}
                  value={settings.website || ''}
                  onChange={e => handleChange('website', e.target.value)}
                />
                <div className="md:col-span-2">
                  <Input
                    label={t('admin.settings.address')}
                    value={settings.address}
                    onChange={e => handleChange('address', e.target.value)}
                  />
                </div>
                <Input
                  label="Ciudad"
                  icon={<MapPin size={16}/>}
                  value={settings.city || ''}
                  onChange={e => handleChange('city', e.target.value)}
                />
                <Input
                  label="Estado/Provincia"
                  value={settings.state || ''}
                  onChange={e => handleChange('state', e.target.value)}
                />
                <Input
                  label="Código Postal"
                  value={settings.postalCode || ''}
                  onChange={e => handleChange('postalCode', e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">País</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                    value={settings.country || 'MX'}
                    onChange={e => handleChange('country', e.target.value)}
                  >
                    <option value="MX">México</option>
                    <option value="US">Estados Unidos</option>
                    <option value="CO">Colombia</option>
                    <option value="PE">Perú</option>
                    <option value="CL">Chile</option>
                    <option value="AR">Argentina</option>
                    <option value="ES">España</option>
                  </select>
                </div>
                <Input
                  label={t('admin.settings.contact_email')}
                  icon={<Mail size={16}/>}
                  value={settings.contactEmail}
                  onChange={e => handleChange('contactEmail', e.target.value)}
                />
                <Input
                  label={t('admin.settings.contact_phone')}
                  icon={<Phone size={16}/>}
                  value={settings.contactPhone}
                  onChange={e => handleChange('contactPhone', e.target.value)}
                />
                <Input
                  label="Email de Soporte"
                  icon={<Mail size={16}/>}
                  value={settings.supportEmail || ''}
                  onChange={e => handleChange('supportEmail', e.target.value)}
                />
              </fieldset>
            </div>

            {/* Rules & Limits */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-slate-800 flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-50 rounded text-emerald-600"><Settings size={18} /></div>
                  {t('admin.settings.section.rules')}
                </h4>
                {canUpdateSettings && (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Save size={16} />}
                    isLoading={savingSection === 'rules'}
                    onClick={() => handleSaveSection('rules')}
                  >
                    Guardar
                  </Button>
                )}
              </div>
              <fieldset disabled={!canUpdateSettings} className="contents">

              {/* Financial Configuration */}
              <div className="mb-6">
                <h5 className="text-sm font-semibold text-slate-600 flex items-center gap-2 mb-3">
                  <DollarSign size={16} /> Configuración Financiera
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Input
                    label={t('admin.settings.maintenance_fee')}
                    type="number"
                    value={settings.defaultMaintenanceFee}
                    onChange={e => {
                      const val = e.target.value;
                      handleChange('defaultMaintenanceFee', val === '' ? 0 : parseFloat(val) || 0);
                    }}
                  />
                  <Input
                    label="% Cargo por Mora"
                    type="number"
                    value={settings.lateFeePercentage ?? ''}
                    onChange={e => handleChange('lateFeePercentage', parseFloat(e.target.value) || 0)}
                  />
                  <Input
                    label="Días de Gracia"
                    type="number"
                    value={settings.lateFeeGraceDays ?? ''}
                    onChange={e => handleChange('lateFeeGraceDays', parseInt(e.target.value) || 0)}
                  />
                  <Input
                    label="Día de Vencimiento"
                    type="number"
                    value={settings.paymentDueDay ?? ''}
                    onChange={e => handleChange('paymentDueDay', Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))}
                  />
                </div>
              </div>

              {/* Limits */}
              <div className="mb-6">
                <h5 className="text-sm font-semibold text-slate-600 flex items-center gap-2 mb-3">
                  <BarChart3 size={16} /> Límites del Sistema
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Input
                    label="Máx. Vehículos/Unidad"
                    type="number"
                    value={settings.maxVehiclesPerUnit ?? ''}
                    onChange={e => handleChange('maxVehiclesPerUnit', parseInt(e.target.value) || 0)}
                  />
                  <Input
                    label="Máx. Mascotas/Unidad"
                    type="number"
                    value={settings.maxPetsPerUnit ?? ''}
                    onChange={e => handleChange('maxPetsPerUnit', parseInt(e.target.value) || 0)}
                  />
                  <Input
                    label={t('admin.settings.visitor_limit')}
                    type="number"
                    value={settings.visitorLimitPerUnit ?? ''}
                    onChange={e => handleChange('visitorLimitPerUnit', parseInt(e.target.value) || 0)}
                  />
                  <Input
                    label="Máx. Invitados Activos"
                    type="number"
                    value={settings.maxActiveGuests ?? ''}
                    onChange={e => handleChange('maxActiveGuests', parseInt(e.target.value) || 0)}
                  />
                  <Input
                    label="Máx. Reservaciones/Día"
                    type="number"
                    value={settings.maxReservationsPerDay ?? ''}
                    onChange={e => handleChange('maxReservationsPerDay', parseInt(e.target.value) || 0)}
                  />
                  <Input
                    label="Días Anticipación Reserva"
                    type="number"
                    value={settings.maxAdvanceReservationDays ?? ''}
                    onChange={e => handleChange('maxAdvanceReservationDays', parseInt(e.target.value) || 0)}
                  />
                  <Input
                    label="Mín. Horas Cancelación"
                    type="number"
                    value={settings.minCancellationHours ?? ''}
                    onChange={e => handleChange('minCancellationHours', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>

              {/* Contact Phones */}
              <div className="mb-6">
                <h5 className="text-sm font-semibold text-slate-600 flex items-center gap-2 mb-3">
                  <Phone size={16} /> Teléfonos de Contacto
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label={t('admin.settings.emergency_phone')}
                    icon={<Phone size={16}/>}
                    value={settings.emergencyPhone}
                    onChange={e => handleChange('emergencyPhone', e.target.value)}
                  />
                  <Input
                    label="Teléfono de Seguridad"
                    icon={<Shield size={16}/>}
                    value={settings.securityPhone || ''}
                    onChange={e => handleChange('securityPhone', e.target.value)}
                  />
                  <Input
                    label="Teléfono de Administración"
                    icon={<Building size={16}/>}
                    value={settings.adminPhone || ''}
                    onChange={e => handleChange('adminPhone', e.target.value)}
                  />
                </div>
              </div>

              {/* Hours */}
              <div>
                <h5 className="text-sm font-semibold text-slate-600 flex items-center gap-2 mb-3">
                  <Clock size={16} /> Horarios
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Input
                    label="Silencio Inicio"
                    type="time"
                    value={settings.quietHoursStart || ''}
                    onChange={e => handleChange('quietHoursStart', e.target.value)}
                  />
                  <Input
                    label="Silencio Fin"
                    type="time"
                    value={settings.quietHoursEnd || ''}
                    onChange={e => handleChange('quietHoursEnd', e.target.value)}
                  />
                  <Input
                    label="Entrada Visitantes Inicio"
                    type="time"
                    value={settings.guestEntryStart || ''}
                    onChange={e => handleChange('guestEntryStart', e.target.value)}
                  />
                  <Input
                    label="Entrada Visitantes Fin"
                    type="time"
                    value={settings.guestEntryEnd || ''}
                    onChange={e => handleChange('guestEntryEnd', e.target.value)}
                  />
                </div>
              </div>
              </fieldset>
            </div>

            {/* Regional Configuration */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-slate-800 flex items-center gap-2">
                  <div className="p-1.5 bg-sky-50 rounded text-sky-600"><Globe size={18} /></div>
                  Configuración Regional
                </h4>
                {canUpdateSettings && (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Save size={16} />}
                    isLoading={savingSection === 'regional'}
                    onClick={() => handleSaveSection('regional')}
                  >
                    Guardar
                  </Button>
                )}
              </div>
              <fieldset disabled={!canUpdateSettings} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Zona Horaria</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                    value={settings.timezone || 'America/Mexico_City'}
                    onChange={e => handleChange('timezone', e.target.value)}
                  >
                    <option value="America/Mexico_City">Ciudad de México (GMT-6)</option>
                    <option value="America/Tijuana">Tijuana (GMT-8)</option>
                    <option value="America/Cancun">Cancún (GMT-5)</option>
                    <option value="America/Monterrey">Monterrey (GMT-6)</option>
                    <option value="America/Hermosillo">Hermosillo (GMT-7)</option>
                    <option value="America/Chihuahua">Chihuahua (GMT-7)</option>
                    <option value="America/Bogota">Bogotá (GMT-5)</option>
                    <option value="America/Lima">Lima (GMT-5)</option>
                    <option value="America/Santiago">Santiago (GMT-4)</option>
                    <option value="America/Buenos_Aires">Buenos Aires (GMT-3)</option>
                    <option value="America/New_York">Nueva York (GMT-5)</option>
                    <option value="America/Los_Angeles">Los Ángeles (GMT-8)</option>
                    <option value="Europe/Madrid">Madrid (GMT+1)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Idioma</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                    value={settings.language || 'es'}
                    onChange={e => handleChange('language', e.target.value)}
                  >
                    <option value="es">Español</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Formato de Fecha</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                    value={settings.dateFormat || 'DD/MM/YYYY'}
                    onChange={e => handleChange('dateFormat', e.target.value)}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2025)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2025)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (2025-12-31)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Formato de Hora</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                    value={settings.timeFormat || '24h'}
                    onChange={e => handleChange('timeFormat', e.target.value)}
                  >
                    <option value="24h">24 horas (14:30)</option>
                    <option value="12h">12 horas (2:30 PM)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Moneda</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                    value={settings.currency || 'MXN'}
                    onChange={e => handleChange('currency', e.target.value)}
                  >
                    <option value="MXN">MXN - Peso Mexicano</option>
                    <option value="USD">USD - Dólar Estadounidense</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="COP">COP - Peso Colombiano</option>
                    <option value="PEN">PEN - Sol Peruano</option>
                    <option value="CLP">CLP - Peso Chileno</option>
                    <option value="ARS">ARS - Peso Argentino</option>
                  </select>
                </div>
                <Input
                  label="Símbolo de Moneda"
                  value={settings.currencySymbol || '$'}
                  onChange={e => handleChange('currencySymbol', e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Primer Día de la Semana</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                    value={settings.firstDayOfWeek ?? 1}
                    onChange={e => handleChange('firstDayOfWeek', parseInt(e.target.value))}
                  >
                    <option value={0}>Domingo</option>
                    <option value={1}>Lunes</option>
                  </select>
                </div>
              </fieldset>
            </div>

            {/* Feature Toggles */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-slate-800 flex items-center gap-2">
                  <div className="p-1.5 bg-green-50 rounded text-green-600"><ToggleLeft size={18} /></div>
                  Módulos Activos
                </h4>
                {canUpdateSettings && (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Save size={16} />}
                    isLoading={savingSection === 'features'}
                    onClick={() => handleSaveSection('features')}
                  >
                    Guardar
                  </Button>
                )}
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Activa o desactiva funcionalidades del sistema para tu condominio.
              </p>
              <fieldset disabled={!canUpdateSettings} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { key: 'enablePayments' as const, label: 'Pagos y Cobranza', icon: CreditCard, color: 'text-violet-600', bg: 'bg-violet-50' },
                  { key: 'enableAmenities' as const, label: 'Amenidades', icon: Calendar, color: 'text-blue-600', bg: 'bg-blue-50' },
                  { key: 'enableGuests' as const, label: 'Visitantes', icon: Users, color: 'text-teal-600', bg: 'bg-teal-50' },
                  { key: 'enableVehicles' as const, label: 'Vehículos', icon: Car, color: 'text-amber-600', bg: 'bg-amber-50' },
                  { key: 'enablePets' as const, label: 'Mascotas', icon: Dog, color: 'text-pink-600', bg: 'bg-pink-50' },
                  { key: 'enableDocuments' as const, label: 'Documentos', icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                  { key: 'enableTickets' as const, label: 'Tickets / Soporte', icon: MessageSquare, color: 'text-orange-600', bg: 'bg-orange-50' },
                  { key: 'enableCommunication' as const, label: 'Comunicaciones', icon: Bell, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                  { key: 'enablePolls' as const, label: 'Encuestas / Votaciones', icon: Vote, color: 'text-cyan-600', bg: 'bg-cyan-50' },
                  { key: 'enableAccessControl' as const, label: 'Control de Acceso', icon: Key, color: 'text-slate-600', bg: 'bg-slate-50' },
                  { key: 'enableRestrictions' as const, label: 'Restricciones', icon: Lock, color: 'text-red-600', bg: 'bg-red-50' },
                  { key: 'enableEmergency' as const, label: 'Emergencias', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
                  { key: 'enableLogbook' as const, label: 'Bitácora', icon: ClipboardList, color: 'text-gray-600', bg: 'bg-gray-50' },
                  { key: 'enablePatrol' as const, label: 'Rondines', icon: MapPin, color: 'text-green-600', bg: 'bg-green-50' },
                  { key: 'enableDirectory' as const, label: 'Directorio', icon: Book, color: 'text-blue-600', bg: 'bg-blue-50' },
                  { key: 'enableSuppliers' as const, label: 'Proveedores', icon: Truck, color: 'text-yellow-600', bg: 'bg-yellow-50' },
                  { key: 'enableInventory' as const, label: 'Inventario', icon: Package, color: 'text-purple-600', bg: 'bg-purple-50' },
                  { key: 'enableReports' as const, label: 'Reportes', icon: BarChart3, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                ].map(({ key, label, icon: Icon, color, bg }) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 cursor-pointer transition-all"
                  >
                    <input
                      type="checkbox"
                      checked={settings.features?.[key] ?? true}
                      onChange={(e) => handleFeatureChange(key, e.target.checked)}
                      className="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className={`p-1.5 rounded ${bg}`}>
                      <Icon size={16} className={color} />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{label}</span>
                  </label>
                ))}
              </fieldset>
            </div>
          </div>
        )}

        {/* FINANCIAL TAB */}
        {activeTab === 'FINANCIAL' && (
          <div className="space-y-6 animate-in fade-in">
            {/* Admin global: elige la sección cuya cuenta quiere ver o cambiar. */}
            <SectionSelector label="Sección" className="max-w-xs" />

            {/* Cuenta de la sección activa: si tiene propia, es la única con la que se cobra. */}
            {selectedSectionId && (
              <SectionBankAccountCard
                key={selectedSectionId}
                sectionId={selectedSectionId}
                sectionName={selectedSectionName}
                canEdit={canEditSectionBank}
              />
            )}

            {/* Bank Info: la del residencial, fallback de las secciones sin cuenta propia. */}
            {!scopedToSection && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-slate-800 flex items-center gap-2">
                    <div className="p-1.5 bg-indigo-50 rounded text-indigo-600"><Landmark size={18} /></div>
                    {t('admin.settings.bank.title')} · Residencial (general)
                  </h4>
                  {canUpdateSettings && (
                    <Button
                      size="sm"
                      variant="primary"
                      icon={<Save size={16} />}
                      isLoading={savingSection === 'bankInfo'}
                      onClick={() => handleSaveSection('bankInfo')}
                    >
                      Guardar
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500 -mt-2 mb-4">
                  La usan las secciones que no tienen cuenta propia y los cargos sin unidad.
                </p>
                <fieldset disabled={!canUpdateSettings} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Input
                    label={t('admin.settings.bank.name')}
                    value={settings.bankInfo?.bankName || ''}
                    onChange={e => handleNestedChange('bankInfo', 'bankName', e.target.value)}
                  />
                  <Input
                    label={t('admin.settings.bank.account')}
                    value={settings.bankInfo?.accountName || ''}
                    onChange={e => handleNestedChange('bankInfo', 'accountName', e.target.value)}
                  />
                  <Input
                    label={t('admin.settings.bank.number')}
                    value={settings.bankInfo?.accountNumber || ''}
                    onChange={e => handleNestedChange('bankInfo', 'accountNumber', e.target.value)}
                  />
                  <Input
                    label={t('admin.settings.bank.clabe')}
                    value={settings.bankInfo?.clabe || ''}
                    onChange={e => handleNestedChange('bankInfo', 'clabe', e.target.value)}
                  />
                  <Input
                    label="Código SWIFT"
                    value={settings.bankInfo?.swift || ''}
                    onChange={e => handleNestedChange('bankInfo', 'swift', e.target.value)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Moneda de la Cuenta</label>
                    <select
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                      value={settings.bankInfo?.currency || 'MXN'}
                      onChange={e => handleNestedChange('bankInfo', 'currency', e.target.value)}
                    >
                      <option value="MXN">MXN - Peso Mexicano</option>
                      <option value="USD">USD - Dólar Estadounidense</option>
                      <option value="EUR">EUR - Euro</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Instrucciones de Pago</label>
                    <textarea
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                      rows={3}
                      value={settings.bankInfo?.paymentInstructions || ''}
                      onChange={e => handleNestedChange('bankInfo', 'paymentInstructions', e.target.value)}
                      placeholder="Instrucciones adicionales para realizar pagos..."
                    />
                  </div>
                </fieldset>
              </div>
            )}

            {/* Fiscal Data */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-slate-800 flex items-center gap-2">
                  <div className="p-1.5 bg-teal-50 rounded text-teal-600"><Receipt size={18} /></div>
                  {t('admin.settings.fiscal.title')}
                </h4>
                {canUpdateSettings && (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Save size={16} />}
                    isLoading={savingSection === 'fiscalData'}
                    onClick={() => handleSaveSection('fiscalData')}
                  >
                    Guardar
                  </Button>
                )}
              </div>
              <fieldset disabled={!canUpdateSettings} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label={t('admin.settings.fiscal.rfc')}
                  value={settings.fiscalData?.rfc || ''}
                  onChange={e => handleNestedChange('fiscalData', 'rfc', e.target.value)}
                />
                <Input
                  label={t('admin.settings.fiscal.name')}
                  value={settings.fiscalData?.businessName || ''}
                  onChange={e => handleNestedChange('fiscalData', 'businessName', e.target.value)}
                />
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t('admin.settings.fiscal.regime')}
                  </label>
                  <div className="relative">
                    <select
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
                      value={settings.fiscalData?.taxRegime || ''}
                      onChange={e => handleNestedChange('fiscalData', 'taxRegime', e.target.value)}
                    >
                      <option value="">Seleccione un régimen...</option>
                      {SAT_REGIMES.map((regime) => (
                        <option key={regime.code} value={`${regime.code} - ${regime.description}`}>
                          {regime.code} - {regime.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <Input
                  label={t('admin.settings.fiscal.zip')}
                  value={settings.fiscalData?.zipCode || ''}
                  onChange={e => handleNestedChange('fiscalData', 'zipCode', e.target.value)}
                />
                <div className="md:col-span-2">
                  <Input
                    label="Dirección Fiscal"
                    value={settings.fiscalData?.address || ''}
                    onChange={e => handleNestedChange('fiscalData', 'address', e.target.value)}
                  />
                </div>
                <Input
                  label="Email para Facturación"
                  icon={<Mail size={16}/>}
                  value={settings.fiscalData?.email || ''}
                  onChange={e => handleNestedChange('fiscalData', 'email', e.target.value)}
                />
              </fieldset>
            </div>
          </div>
        )}

        {/* INTEGRATIONS TAB */}
        {activeTab === 'INTEGRATIONS' && (
          <IntegrationsTab canUpdate={canUpdateSettings} />
        )}

        {/* BILLING TAB */}
        {activeTab === 'BILLING' && (
          <div className="animate-in fade-in" key={billingRefreshKey}>
            <BillingTab
              canUpdate={canUpdateSettings}
              onChangePlan={canUpdateSettings ? () => setShowChangePlanModal(true) : undefined}
              onAddPaymentMethod={canUpdateSettings ? () => setShowAddPaymentModal(true) : undefined}
              onViewInvoice={(invoice) => {
                setSelectedInvoice(invoice);
                setShowInvoiceDetailModal(true);
              }}
              onPayInvoice={canUpdateSettings ? (invoice) => {
                setSelectedInvoice(invoice);
                setShowPayInvoiceModal(true);
              } : undefined}
            />
          </div>
        )}

        {activeTab !== 'BILLING' && (
          <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3 text-sm text-blue-800 mt-8">
            <Info className="shrink-0 mt-0.5" size={18} />
            <p>
              Los cambios en la configuración pueden tardar unos minutos en reflejarse en la aplicación móvil de los residentes. Asegúrese de que las credenciales de integración sean correctas para evitar interrupciones en el servicio.
            </p>
          </div>
        )}
      </div>

      {/* Billing Modals */}
      {canUpdateSettings && showChangePlanModal && (
        <ChangePlanModal
          isOpen={showChangePlanModal}
          onClose={() => setShowChangePlanModal(false)}
          currentSubscription={{} as any} // Will be loaded by the component
          onPlanChanged={() => setBillingRefreshKey(k => k + 1)}
        />
      )}

      {canUpdateSettings && (
        <AddPaymentMethodModal
          isOpen={showAddPaymentModal}
          onClose={() => setShowAddPaymentModal(false)}
          onSuccess={() => setBillingRefreshKey(k => k + 1)}
        />
      )}

      {canUpdateSettings && selectedInvoice && (
        <>
          <InvoiceDetailModal
            isOpen={showInvoiceDetailModal}
            onClose={() => {
              setShowInvoiceDetailModal(false);
              setSelectedInvoice(null);
            }}
            invoiceId={selectedInvoice.id}
            onPay={() => {
              setShowInvoiceDetailModal(false);
              setShowPayInvoiceModal(true);
            }}
          />

          <PayInvoiceModal
            isOpen={showPayInvoiceModal}
            onClose={() => {
              setShowPayInvoiceModal(false);
              setSelectedInvoice(null);
            }}
            invoice={selectedInvoice}
            onSuccess={() => setBillingRefreshKey(k => k + 1)}
            onAddPaymentMethod={() => {
              setShowPayInvoiceModal(false);
              setShowAddPaymentModal(true);
            }}
          />
        </>
      )}
    </div>
  );
};
